import type { DbClient } from './types.js';

export const AI_SUMMARY_TYPES = [
  'docType',
  'docContent',
  'scAnalysis',
] as const;
export type AiSummaryType = (typeof AI_SUMMARY_TYPES)[number];

const TYPE_COLUMN: Record<AiSummaryType, string> = {
  docType: 'doc_type',
  docContent: 'doc_content',
  scAnalysis: 'sc_analysis',
};

// A type is still actively running while queued or processing.
export const IN_FLIGHT = new Set(['PREPARING', 'PROCESSING']);
// Terminal states count toward the overall completion percentage.
const TERMINAL = new Set(['PROCESSED', 'FAILED', 'CANCELLED']);

const CANCELLED_VALUE = JSON.stringify({ status: 'CANCELLED', result: null });

export interface AiSummaryCancelState {
  found: boolean;
  summaryId: string | null;
  statusByType: Record<AiSummaryType, string | null>;
  // When each type entered its current in-flight phase. Anchors the per-type
  // cancel time-gate so cancelling/re-running one type never resets another's
  // clock. Null for terminal types or legacy rows written before it was stamped.
  startedAtByType: Record<AiSummaryType, string | null>;
}

/**
 * Read the per-type status + per-type processing-start timestamp for an order's
 * AI summary, resolved by the stable (tracing_type, tracing_id) unique key and
 * company scope.
 */
export const readAiSummaryCancelState = async (
  db: DbClient,
  {
    tracingId,
    tracingType,
    companyId,
  }: { tracingId: string; tracingType: string; companyId: string },
): Promise<AiSummaryCancelState> => {
  const rows = await db.runSql<{
    id: string;
    docTypeStatus: string | null;
    docContentStatus: string | null;
    scAnalysisStatus: string | null;
    docTypeStartedAt: string | null;
    docContentStartedAt: string | null;
    scAnalysisStartedAt: string | null;
  }>(
    `SELECT id,
            doc_type->>'status'       AS doc_type_status,
            doc_content->>'status'    AS doc_content_status,
            sc_analysis->>'status'    AS sc_analysis_status,
            doc_type->>'startedAt'    AS doc_type_started_at,
            doc_content->>'startedAt' AS doc_content_started_at,
            sc_analysis->>'startedAt' AS sc_analysis_started_at
     FROM tracing_ai_summaries
     WHERE tracing_type = $1 AND tracing_id = $2 AND company_id = $3`,
    [tracingType, tracingId, companyId],
  );

  const row = rows[0];
  if (!row) {
    return {
      found: false,
      summaryId: null,
      statusByType: { docType: null, docContent: null, scAnalysis: null },
      startedAtByType: { docType: null, docContent: null, scAnalysis: null },
    };
  }

  return {
    found: true,
    summaryId: row.id,
    statusByType: {
      docType: row.docTypeStatus,
      docContent: row.docContentStatus,
      scAnalysis: row.scAnalysisStatus,
    },
    startedAtByType: {
      docType: row.docTypeStartedAt,
      docContent: row.docContentStartedAt,
      scAnalysis: row.scAnalysisStartedAt,
    },
  };
};

export interface AiSummaryCancelResult {
  cancelledTypes: AiSummaryType[];
  allTerminal: boolean;
  percentage: number;
}

/**
 * Mark the requested in-flight types as CANCELLED (leaving terminal types
 * untouched) and recompute the summary's overall completion.
 *
 * - `summaryTypes` omitted/empty → cancel ALL in-flight types (whole-summary cancel).
 * - `summaryTypes` provided → cancel only those that are in-flight (per-type cancel).
 */
export const applyAiSummaryCancellation = async (
  db: DbClient,
  {
    summaryId,
    statusByType,
    summaryTypes,
  }: {
    summaryId: string;
    statusByType: Record<AiSummaryType, string | null>;
    summaryTypes?: AiSummaryType[];
  },
): Promise<AiSummaryCancelResult> => {
  const requested = summaryTypes?.length ? summaryTypes : [...AI_SUMMARY_TYPES];
  const cancelledTypes = requested.filter(type =>
    IN_FLIGHT.has(statusByType[type] ?? ''),
  );

  if (cancelledTypes.length > 0) {
    const setClauses = cancelledTypes.map(
      (type, i) => `${TYPE_COLUMN[type]} = $${i + 2}::jsonb`,
    );
    // Only the per-type column is rewritten (status → CANCELLED). The shared
    // `updated_at` is intentionally left untouched: the cancel time-gate is
    // anchored on each type's own `startedAt`, so nothing here should affect a
    // sibling type's clock.
    await db.runSql(
      `UPDATE tracing_ai_summaries SET ${setClauses.join(', ')} WHERE id = $1`,
      [summaryId, ...cancelledTypes.map(() => CANCELLED_VALUE)],
    );
  }

  // Recompute overall state from the post-update statuses (cancelled types are
  // now CANCELLED; everything else is unchanged).
  const finalStatuses = { ...statusByType };
  for (const type of cancelledTypes) finalStatuses[type] = 'CANCELLED';

  const nonNull = AI_SUMMARY_TYPES.map(t => finalStatuses[t]).filter(
    (s): s is string => s != null,
  );
  const terminalCount = nonNull.filter(s => TERMINAL.has(s)).length;
  const allTerminal = nonNull.length > 0 && terminalCount === nonNull.length;
  const percentage =
    nonNull.length === 0
      ? 0
      : Math.round((terminalCount / nonNull.length) * 100);

  return { cancelledTypes, allTerminal, percentage };
};
